import { apiSantoLog } from '../../../Services/api'

/**
 * Geração e impressão do Romaneio de Embarque.
 *
 * O backend (GET /api/santolog/romaneio) entrega o JSON pronto:
 *   { date, drivers: [{ driver, totals, orders: [{ ..., volumes: [...] }] }], totals }
 *
 * Aqui montamos um documento HTML branco (papel) e abrimos numa nova janela
 * que dispara window.print(). Independente do tema escuro do app.
 *
 * IMPORTANTE (timezone): as datas vêm com "Z" mas já são horário local correto.
 * Só formatamos a string da data (YYYY-MM-DD → DD/MM/YYYY); não há conversão.
 */

// ─── HELPERS DE FORMATAÇÃO ──────────────────────────────────────────────────
function fmtDateBR(isoDate) {
  if (!isoDate) return '—'
  const m = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return String(isoDate)
  const [, y, mo, d] = m
  return `${d}/${mo}/${y}`
}

// Endereço de entrega compacto: "Rua, complemento — Bairro - Cidade/UF".
// Aceita endereço aninhado (order.address) ou campos flat no próprio pedido.
function formatAddress(order) {
  const a = order.address || order || {}
  const line1 = [a.street, a.complement].filter(Boolean).join(', ')
  const line2 = [a.neighborhood, [a.city, a.state_uf].filter(Boolean).join('/')].filter(Boolean).join(' - ')
  const full = [line1, line2].filter(Boolean).join(' — ')
  return full || '—'
}

function fmtPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '')
  if (d.length === 11) return d.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  if (d.length === 10) return d.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
  return raw ? String(raw) : '—'
}

// Telefone do cliente. O backend deve enviar em um destes campos.
function getPhone(order) {
  const raw =
    order.recipient_phone ?? order.phone ?? order.telefone ??
    order.recipient_telephone ?? order.cellphone ?? null
  return raw ? fmtPhone(raw) : '—'
}

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// NF do pedido: nfe_number únicos dos volumes; cai pro order_number se não houver.
function nfFromOrder(order) {
  const vols = Array.isArray(order.volumes) ? order.volumes : []
  const nfs = [...new Set(vols.map((v) => v.nfe_number).filter(Boolean))]
  if (nfs.length) return nfs.join(', ')
  return order.order_number || '—'
}

function volumesCount(order) {
  if (order.volumes_count != null) return order.volumes_count
  return Array.isArray(order.volumes) ? order.volumes.length : 0
}

// ─── TEMPLATE ────────────────────────────────────────────────────────────────
function driverBlockHTML(block, date) {
  const driver = block.driver || {}
  const totals = block.totals || {}
  const orders = Array.isArray(block.orders) ? block.orders : []
  const name = driver.name || 'Sem motorista'

  const rows = orders
    .map(
      (o) => `
      <tr>
        <td class="c">${o.sequence_order != null ? esc(o.sequence_order) : '—'}</td>
        <td class="mono">${esc(nfFromOrder(o))}</td>
        <td>${esc(formatAddress(o))}</td>
        <td class="mono phone">${esc(getPhone(o))}</td>
        <td class="c mono vol">${esc(volumesCount(o))}</td>
        <td class="c mid"><span class="checkbox"></span></td>
        <td class="sig"></td>
      </tr>`
    )
    .join('')

  // O bloco inteiro é UMA <table>. Header (título + meta + kpis) + linha de
  // cabeçalho de colunas ficam dentro de <thead> — o navegador repete <thead>
  // automaticamente em cada página impressa (`display: table-header-group`).
  return `
  <section class="romaneio">
    <table class="grid">
      <thead>
        <tr class="doc-head">
          <th colspan="7">
            <div class="head">
              <div class="title">
                <h1>Romaneio de Embarque</h1>
                <div class="sub">Loja Santo Antônio · SantoLog</div>
              </div>
              <div class="meta">
                <div><span>Motorista</span><strong>${esc(name)}</strong></div>
                <div><span>Data do Embarque</span><strong>${esc(fmtDateBR(date))}</strong></div>
              </div>
            </div>
            <div class="summary">
              <div class="kpi"><span>Pedidos</span><strong>${esc(totals.orders != null ? totals.orders : orders.length)}</strong></div>
              <div class="kpi"><span>Volumes</span><strong>${esc(totals.volumes != null ? totals.volumes : '—')}</strong></div>
            </div>
          </th>
        </tr>
        <tr class="col-head">
          <th class="c">Seq.</th>
          <th>Nota Fiscal</th>
          <th>Endereço</th>
          <th>Telefone</th>
          <th class="c vol">VOL</th>
          <th class="c">Não Coletado</th>
          <th class="c">Assinatura</th>
        </tr>
      </thead>
      <tbody>
        ${rows || '<tr><td colspan="7" class="empty">Nenhum pedido embarcado.</td></tr>'}
      </tbody>
    </table>

    <footer class="signs">
      <div class="sign"><div class="line"></div><span>Assinatura da Transportadora</span></div>
      <div class="sign"><div class="line"></div><span>Assinatura do Motorista</span></div>
    </footer>
  </section>`
}

function documentHTML(payload) {
  const date = payload && payload.date
  const drivers = Array.isArray(payload && payload.drivers) ? payload.drivers : []
  const blocks = drivers.length
    ? drivers.map((b) => driverBlockHTML(b, date)).join('')
    : '<section class="romaneio"><p class="empty">Nenhum embarque encontrado para a data.</p></section>'

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<title>Romaneio ${fmtDateBR(date)}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #111; background: #fff; }
  .romaneio { padding: 14px 18px; page-break-after: always; }
  .romaneio:last-child { page-break-after: auto; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 8px; }
  .head h1 { margin: 0; font-size: 16px; }
  .head .sub { font-size: 10px; color: #555; margin-top: 1px; }
  .head .meta { text-align: right; font-size: 11px; line-height: 1.35; }
  .head .meta span { color: #666; margin-right: 6px; }
  .head .meta strong { color: #111; }
  .summary { display: flex; gap: 22px; margin: 8px 0 10px; }
  .summary .kpi span { display: block; font-size: 9px; text-transform: uppercase; letter-spacing: .05em; color: #666; }
  .summary .kpi strong { font-size: 13px; }
  table.grid { width: 100%; border-collapse: collapse; font-size: 10px; }
  /* Faz o <thead> repetir em CADA página impressa */
  table.grid thead { display: table-header-group; }
  table.grid th, table.grid td { border: 1px solid #999; padding: 2px 6px; text-align: left; vertical-align: top; }
  table.grid th { background: #f0f0f0; font-size: 9px; text-transform: uppercase; letter-spacing: .04em; }
  /* Célula-container do header do documento (título + meta + kpis) — não herda o
     background cinza nem a fonte pequena/uppercase das <th> de coluna. */
  table.grid tr.doc-head > th { background: #fff; padding: 0 0 6px; border: none; text-transform: none; letter-spacing: normal; font-size: 10px; font-weight: normal; text-align: left; }
  table.grid td.c, table.grid th.c { text-align: center; }
  table.grid td.r, table.grid th.r { text-align: right; }
  table.grid td.mono { font-family: 'Courier New', monospace; }
  table.grid td.empty, .empty { text-align: center; color: #888; padding: 14px; }
  table.grid tr { page-break-inside: avoid; }
  /* Telefone: mantém sempre em uma linha (não quebra "(11) 99999-9999") */
  table.grid td.phone { white-space: nowrap; }
  /* Coluna VOL: largura enxuta (só o suficiente pro número) */
  table.grid th.vol, table.grid td.vol { width: 1%; white-space: nowrap; padding-left: 8px; padding-right: 8px; }
  /* Célula com checkbox centralizado vertical + horizontalmente */
  table.grid td.mid { vertical-align: middle; }
  /* Checkbox vazio pra o operador marcar "não coletado" à caneta */
  table.grid .checkbox { display: inline-block; width: 14px; height: 14px; border: 1.5px solid #111; border-radius: 2px; vertical-align: middle; }
  /* Célula da assinatura por linha — mais alta pra dar espaço à caneta */
  table.grid td.sig { min-width: 130px; height: 30px; }
  .signs { display: flex; justify-content: space-between; gap: 48px; margin-top: 22px; page-break-inside: avoid; }
  .signs .sign { flex: 1; text-align: center; }
  .signs .line { border-top: 1px solid #111; margin-top: 34px; margin-bottom: 6px; }
  .signs span { font-size: 10px; color: #333; }
  @media print {
    @page { size: A4 portrait; margin: 10mm; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body onload="window.focus(); window.print();">
  ${blocks}
  <script>window.onafterprint = function () { window.close(); };</script>
</body>
</html>`
}

// ─── API PÚBLICA ─────────────────────────────────────────────────────────────
/**
 * Imprime a partir de um payload já em memória ({ date, drivers }).
 * Retorna { ok, error } — error === 'popup_blocked' quando o navegador bloqueia.
 */
export function printDrivers(payload) {
  const win = window.open('', '_blank', 'width=920,height=720')
  if (!win) return { ok: false, error: 'popup_blocked' }
  win.document.open()
  win.document.write(documentHTML(payload))
  win.document.close()
  return { ok: true }
}

/**
 * Busca no backend e imprime. Use quando não há o payload em memória
 * (ex.: a partir do modal de embarque finalizado).
 *   - driverId presente → romaneio de um motorista.
 *   - driverId ausente   → todos os motoristas do dia.
 *
 * A janela é aberta de forma SÍNCRONA (no gesto de clique) e só depois
 * preenchida com o resultado do fetch — caso contrário o navegador bloqueia
 * o pop-up, já que o window.open aconteceria após o await.
 */
// ─── ROMANEIO SIMPLES (Devolução / Não-embarcados) ──────────────────────────
// Formato do backend: { date, items: [{ linha, pedido, nf, peso, volumes, volumes_list }], totals }
// Colunas: # · Pedido · NF · Peso (kg) · Volumes. Usado após finalizar a devolução
// e para o romaneio de não-embarcados do dia.
function fmtPeso(p) {
  if (p == null || p === '') return '—'
  const n = Number(p)
  if (Number.isNaN(n)) return String(p)
  return n.toFixed(2).replace('.', ',')
}

function returnDocHTML(payload, opts = {}) {
  const title = opts.title || 'Romaneio de Devolução'
  const date = payload && payload.date
  const items = Array.isArray(payload && payload.items) ? payload.items : []
  const totals = (payload && payload.totals) || {}

  const rows = items
    .map(
      (it) => `
      <tr>
        <td class="c">${esc(it.linha != null ? it.linha : '')}</td>
        <td class="mono">${esc(it.nf || it.pedido || '—')}</td>
        <td class="r mono">${esc(fmtPeso(it.peso))}</td>
        <td class="c mono vol">${esc(it.volumes != null ? it.volumes : (Array.isArray(it.volumes_list) ? it.volumes_list.length : '—'))}</td>
      </tr>`
    )
    .join('')

  const totalsRow = `
      <tr class="tot">
        <td class="c" colspan="2">Totais — ${esc(totals.pedidos != null ? totals.pedidos : items.length)} pedido(s)</td>
        <td class="r mono">${esc(fmtPeso(totals.peso))}</td>
        <td class="c mono vol">${esc(totals.volumes != null ? totals.volumes : '—')}</td>
      </tr>`

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<title>${esc(title)} ${esc(fmtDateBR(date))}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #111; background: #fff; }
  .romaneio { padding: 14px 18px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 8px; }
  .head h1 { margin: 0; font-size: 16px; }
  .head .sub { font-size: 10px; color: #555; margin-top: 1px; }
  .head .meta { text-align: right; font-size: 11px; line-height: 1.35; }
  .head .meta span { color: #666; margin-right: 6px; }
  .summary { display: flex; gap: 22px; margin: 8px 0 10px; }
  .summary .kpi span { display: block; font-size: 9px; text-transform: uppercase; letter-spacing: .05em; color: #666; }
  .summary .kpi strong { font-size: 13px; }
  table.grid { width: 100%; border-collapse: collapse; font-size: 10px; }
  table.grid thead { display: table-header-group; }
  table.grid th, table.grid td { border: 1px solid #999; padding: 3px 6px; text-align: left; vertical-align: top; }
  table.grid th { background: #f0f0f0; font-size: 9px; text-transform: uppercase; letter-spacing: .04em; }
  table.grid td.c, table.grid th.c { text-align: center; }
  table.grid td.r, table.grid th.r { text-align: right; }
  table.grid td.mono { font-family: 'Courier New', monospace; }
  table.grid th.vol, table.grid td.vol { width: 1%; white-space: nowrap; }
  table.grid tr { page-break-inside: avoid; }
  table.grid tr.tot td { background: #f7f7f7; font-weight: bold; }
  .empty { text-align: center; color: #888; padding: 14px; }
  .signs { display: flex; justify-content: space-between; gap: 48px; margin-top: 26px; page-break-inside: avoid; }
  .signs .sign { flex: 1; text-align: center; }
  .signs .line { border-top: 1px solid #111; margin-top: 34px; margin-bottom: 6px; }
  .signs span { font-size: 10px; color: #333; }
  @media print {
    @page { size: A4 portrait; margin: 10mm; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body onload="window.focus(); window.print();">
  <section class="romaneio">
    <div class="head">
      <div class="title">
        <h1>${esc(title)}</h1>
        <div class="sub">Loja Santo Antônio · SantoLog</div>
      </div>
      <div class="meta">
        <div><span>Data</span><strong>${esc(fmtDateBR(date))}</strong></div>
      </div>
    </div>
    <div class="summary">
      <div class="kpi"><span>Pedidos</span><strong>${esc(totals.pedidos != null ? totals.pedidos : items.length)}</strong></div>
      <div class="kpi"><span>Volumes</span><strong>${esc(totals.volumes != null ? totals.volumes : '—')}</strong></div>
      <div class="kpi"><span>Peso (kg)</span><strong>${esc(fmtPeso(totals.peso))}</strong></div>
    </div>
    <table class="grid">
      <thead>
        <tr>
          <th class="c">#</th>
          <th>Nota Fiscal</th>
          <th class="r">Peso (kg)</th>
          <th class="c vol">Volumes</th>
        </tr>
      </thead>
      <tbody>
        ${rows || '<tr><td colspan="4" class="empty">Nenhum item.</td></tr>'}
        ${items.length ? totalsRow : ''}
      </tbody>
    </table>

    <footer class="signs">
      <div class="sign"><div class="line"></div><span>Conferente</span></div>
      <div class="sign"><div class="line"></div><span>Responsável</span></div>
    </footer>
  </section>
  <script>window.onafterprint = function () { window.close(); };</script>
</body>
</html>`
}

/**
 * Imprime um romaneio simples (devolução) a partir de um payload em memória
 * ({ date, items, totals }). Retorna { ok, error }.
 */
export function printReturnRomaneio(payload, opts = {}) {
  const win = window.open('', '_blank', 'width=920,height=720')
  if (!win) return { ok: false, error: 'popup_blocked' }
  win.document.open()
  win.document.write(returnDocHTML(payload, opts))
  win.document.close()
  return { ok: true }
}

/**
 * Busca e imprime o romaneio de NÃO-EMBARCADOS do dia.
 * Abre a janela de forma síncrona (gesto de clique) e preenche após o fetch.
 */
export async function printNaoEmbarcado({ date } = {}) {
  const win = window.open('', '_blank', 'width=920,height=720')
  if (!win) return { ok: false, error: 'popup_blocked' }
  win.document.open()
  win.document.write(
    '<!doctype html><html lang="pt-BR"><head><meta charset="utf-8" />' +
    '<title>Romaneio</title></head>' +
    '<body style="font-family:Arial,Helvetica,sans-serif;padding:40px;color:#555">' +
    'Gerando romaneio de não-embarcados…</body></html>'
  )
  win.document.close()
  try {
    const params = {}
    if (date) params.date = date
    const { data } = await apiSantoLog.get('/romaneio/nao-embarcado', { params })
    win.document.open()
    win.document.write(returnDocHTML(data || { date, items: [], totals: {} }, { title: 'Romaneio de Não-Embarcados' }))
    win.document.close()
    return { ok: true }
  } catch (err) {
    try { win.close() } catch (e) { /* noop */ }
    throw err
  }
}

export async function printRomaneioByDriver({ driverId, date }) {
  const win = window.open('', '_blank', 'width=920,height=720')
  if (!win) return { ok: false, error: 'popup_blocked' }

  win.document.open()
  win.document.write(
    '<!doctype html><html lang="pt-BR"><head><meta charset="utf-8" />' +
    '<title>Romaneio</title></head>' +
    '<body style="font-family:Arial,Helvetica,sans-serif;padding:40px;color:#555">' +
    'Gerando romaneio…</body></html>'
  )
  win.document.close()

  try {
    const params = {}
    if (date) params.date = date
    if (driverId) params.driver_id = driverId
    const { data } = await apiSantoLog.get('/romaneio', { params })
    win.document.open()
    win.document.write(documentHTML(data || { date, drivers: [] }))
    win.document.close()
    return { ok: true }
  } catch (err) {
    try { win.close() } catch (e) { /* noop */ }
    throw err
  }
}
